import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiFileText 
} from 'react-icons/fi';
import { API_BASE_URL } from '../config/config';

const LegalPages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.get(`${API_BASE_URL}/legal-pages`, {
        headers: { 'X-User-ID': userId }
      });
      
      if (response.data.success) {
        setPages(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching legal pages:', error);
      toast.error('Error loading legal pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;

    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.delete(`${API_BASE_URL}/legal-page/${id}`, {
        headers: { 'X-User-ID': userId }
      });

      if (response.data.success) {
        toast.success('Page deleted successfully');
        fetchPages();
      }
    } catch (error) {
      toast.error('Error deleting page');
    }
  };

  const toggleStatus = async (id) => {
    try {
      const userId = localStorage.getItem('institute_user') ? 
        JSON.parse(localStorage.getItem('institute_user')).id : null;
      
      const response = await axios.post(
        `${API_BASE_URL}/legal-page/${id}/toggle-status`,
        {},
        { headers: { 'X-User-ID': userId } }
      );

      if (response.data.success) {
        toast.success('Page status updated');
        fetchPages();
      }
    } catch (error) {
      toast.error('Error updating page status');
    }
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const truncateText = (text, maxLength = 150) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  };

  const getPageTypeLabel = (pageType) => {
    const labels = {
      'privacy_policy': 'Privacy Policy',
      'terms_conditions': 'Terms & Conditions',
      'refund_policy': 'Refund Policy',
      'about_us': 'About Us',
      'contact_us': 'Contact Us',
      'other': 'Other'
    };
    return labels[pageType] || pageType;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Legal Pages</h1>
          <p className="text-gray-600">Manage privacy policy, terms & conditions, and other legal pages</p>
        </div>
        <Link
          to="/legal-pages/create"
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus /> Create New Page
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : pages.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <FiFileText size={64} className="mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No legal pages yet</h3>
          <p className="text-gray-600 mb-6">Create privacy policy, terms & conditions, and other important pages</p>
          <Link
            to="/legal-pages/create"
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus /> Create Your First Page
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {pages.map((page) => (
            <div key={page.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              <div className="flex">
                {page.feature_image && (
                  <div className="w-64 h-48 flex-shrink-0">
                    <img 
                      src={page.feature_image} 
                      alt={page.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="flex-1 p-6">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{page.title}</h3>
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                          {getPageTypeLabel(page.page_type)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">
                        URL: <span className="text-blue-600">{page.url}</span>
                      </p>
                      {page.description && (
                        <p className="text-gray-600 text-sm mb-3">
                          {truncateText(stripHtml(page.description), 200)}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => toggleStatus(page.id)}
                        className={`p-2 rounded ${
                          page.is_active == 1 
                            ? 'bg-green-100 text-green-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                        title={page.is_active == 1 ? 'Published' : 'Draft'}
                      >
                        {page.is_active == 1 ? <FiEye size={20} /> : <FiEyeOff size={20} />}
                      </button>
                      
                      <Link
                        to={`/legal-pages/edit/${page.id}`}
                        className="p-2 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                      >
                        <FiEdit2 size={20} />
                      </Link>
                      
                      <button
                        onClick={() => handleDelete(page.id)}
                        className="p-2 rounded bg-red-100 text-red-600 hover:bg-red-200"
                      >
                        <FiTrash2 size={20} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    {page.cdate && (
                      <span>Created: {new Date(page.cdate).toLocaleDateString()}</span>
                    )}
                    {page.udate && (
                      <span>Updated: {new Date(page.udate).toLocaleDateString()}</span>
                    )}
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      page.is_active == 1 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {page.is_active == 1 ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LegalPages;
